// Messaging area shell (office, Module 17 §4 — slice P3-B part 2).
// Sub-navigation over the three §4 office screens: the conversations inbox
// (§4.1, with §4.2's unclaimed work as its `unclaimed` tab), the template
// catalog (§4.3) and the ga/fa-only settings screen (§4.4). Registered at
// /messaging; the index lands each principal on the first screen they can
// actually open (`messagingLandingPath`).
//
// WHAT IS DELIBERATELY ABSENT (D-033 / spec 17 §4): no admin-area §4.6 screen
// here (the platform console is another area and another audience), and no
// own-profile §4.5 widget — the client has no `/v1/auth/me` (the role feed
// carries memberships, not a subject — lib/role-codes.ts states it), so a
// screen that needs the caller's own user id cannot exist yet. Stated in the
// link widget's header and the slice report rather than stubbed.
//
// Nav gating mirrors §7 through hooks/permissions.ts, and the entries ride
// the CODES their screens' reads ride (inbox → conversation.read, templates
// → template.read, settings → settings.read) — never a link into a dead 403
// call (D-088). §4.4's rule is grant-shaped: only ga/fa hold settings.read,
// so everyone else simply has no Settings entry.
//
// RENDER-ONCE (§5), AND THE WHOLE AREA AT ONCE — the waste-layout mechanism,
// verbatim: every entry is permission-decided, so the strip's WIDTH (and at
// 412 px its HEIGHT, since it wraps) is decided by a role feed that arrives
// after first paint. While the mirror is outstanding the area is a reserving
// skeleton whose box IS `.ds-tabs`'s pinned min-height; when it settles the
// whole subtree REMOUNTS (`key` swap) so nothing painted early ever moves.

import { useEffect } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton, ForbiddenState } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import {
  messagingAreaEntries,
  messagingLandingPath,
  useMessagingPermissions,
} from "./hooks/permissions";
import "./messaging.css";

export function MessagingLayout() {
  const { t } = useTranslation();
  const perms = useMessagingPermissions();

  // ONE ENTRY TABLE (hooks/permissions.ts) — the strip, the app nav and the
  // index redirect all read it, so an entry cannot exist in one and not the
  // others (the two-gating-rules defect module 05 shipped and fixed).
  const tabs: RouterTabDef[] = messagingAreaEntries(perms).map((entry) => ({
    to: entry.to,
    label: t(entry.labelKey),
    "data-testid": entry.testid,
  }));

  if (!perms.isResolved) {
    return (
      <div className="msg-area" key="messaging-loading" data-testid="messaging-loading">
        <div className="ds-tabs msg-subnav-skel" aria-hidden="true" />
        <div className="msg-body">
          <CardSkeleton lines={6} label={t("messaging.common.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="msg-area" key="messaging-ready">
      <RouterTabs tabs={tabs} label={t("messaging.nav.aria")} data-testid="messaging-subnav" />
      <div className="msg-body">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * `/messaging` — land this principal on the first screen they can actually
 * open.
 *
 * A COMPONENT, not a `beforeLoad`: `beforeLoad` runs outside React and cannot
 * read the role feed, so a static redirect would send a settings-only
 * principal (none exists today, but the rule is the rule) — or, concretely, a
 * qc lead who holds `conversation.read` and nothing else — to a screen they
 * are refused (the module-05 defect, fixed there, not re-shipped here). The
 * parent shell has already reserved the layout, so this renders nothing while
 * it decides.
 */
export function MessagingIndexRedirect() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useMessagingPermissions();
  const to = messagingLandingPath(perms);

  useEffect(() => {
    if (to !== undefined) void navigate({ to, replace: true });
  }, [to, navigate]);

  if (to !== undefined) return null;
  return <ForbiddenState title={t("messaging.access.title")} body={t("messaging.access.area")} />;
}
